import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { getCurrentUserCompanyId } from "@/lib/userUtils";

interface BalanceSheetItemDialogProps {
  subsection: "Assets" | "Liabilities" | "Equity";
  onSuccess: () => void;
}

export const BalanceSheetItemDialog = ({ subsection, onSuccess }: BalanceSheetItemDialogProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    item_type: "",
    description: "",
    amount: "",
    date: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        toast.error("Company not found");
        return;
      }

      const { error } = await supabase
        .from('balance_sheet_items')
        .insert({
          company_id: companyId,
          subsection: subsection,
          item_type: formData.item_type,
          description: formData.description,
          amount: parseFloat(formData.amount),
          date: formData.date
        });

      if (error) throw error;

      toast.success(`${subsection} item added successfully`);
      setOpen(false);
      setFormData({
        item_type: "",
        description: "",
        amount: "",
        date: new Date().toISOString().split('T')[0]
      });
      onSuccess();
    } catch (error) {
      console.error("Error adding balance sheet item:", error);
      toast.error("Failed to add item");
    } finally {
      setLoading(false);
    }
  };

  const getItemTypeOptions = () => {
    switch (subsection) {
      case "Assets":
        return ["Cash", "Accounts Receivable", "Inventory", "Equipment", "Property", "Other Assets"];
      case "Liabilities":
        return ["Accounts Payable", "Loans Payable", "Credit Card Debt", "Accrued Expenses", "Other Liabilities"];
      case "Equity":
        return ["Owner's Capital", "Retained Earnings", "Drawings", "Other Equity"];
      default:
        return [];
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add {subsection}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add {subsection} Item</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="item_type">Type</Label>
            <Select
              value={formData.item_type}
              onValueChange={(value) => setFormData({ ...formData, item_type: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {getItemTypeOptions().map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter description"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Adding..." : "Add Item"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
